import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { Department } from 'maestro-shared/referential/Department';
import { Region } from 'maestro-shared/referential/Region';
import { Stage } from 'maestro-shared/referential/Stage';
import { LocalPrescriptionComment } from 'maestro-shared/schema/LocalPrescription/LocalPrescriptionComment';
import { MatrixKind } from 'maestro-shared/schema/Matrix/MatrixReferential';
import { Prescription } from 'maestro-shared/schema/Prescription/Prescription';
import { PrescriptionComments } from 'maestro-shared/schema/Prescription/PrescriptionComments';
import { ProgrammingPlanContext } from 'maestro-shared/schema/ProgrammingPlan/Context';
import { ProgrammingPlanDomainId } from 'maestro-shared/schema/ProgrammingPlan/ProgrammingPlanDomain';
import { ProgrammingPlanChecked } from 'maestro-shared/schema/ProgrammingPlan/ProgrammingPlans';
import { ProgrammingSubPlanId } from 'maestro-shared/schema/ProgrammingPlan/ProgrammingSubPlan';
import { z } from 'zod';

export const PrescriptionFilters = z.object({
  year: z.coerce.number().int().nullish(),
  stage: Stage.nullish(),
  programmingPlanIds: z.array(z.guid()).nullish(),
  programmingSubPlanIds: z.array(ProgrammingSubPlanId).nullish(),
  programmingPlanDomainIds: z.array(ProgrammingPlanDomainId).nullish(),
  matrixKinds: z.array(MatrixKind).nullish(),
  contexts: z.array(ProgrammingPlanContext).nullish(),
  coordinatorIds: z.array(z.guid()).nullish(),
  laboratoryIds: z.array(z.guid()).nullish(),
  missingDistribution: z.boolean().nullish(),
  missingLaboratory: z.boolean().nullish(),
  withNovelty: z.boolean().nullish()
});

export type PrescriptionFilters = z.infer<typeof PrescriptionFilters>;

const PrescriptionCommentsData = z.discriminatedUnion('viewBy', [
  z.object({
    viewBy: z.literal('Prescription'),
    programmingPlan: ProgrammingPlanChecked,
    prescription: Prescription,
    currentRegion: Region.nullish(),
    regionalCommentsList: z.array(
      z.object({
        region: Region,
        department: Department.nullish(),
        comments: z
          .array(
            LocalPrescriptionComment.pick({
              comment: true,
              createdAt: true,
              createdBy: true
            })
          )
          .min(1)
      })
    )
  }),
  z.object({
    viewBy: z.literal('Region'),
    region: Region,
    currentPrescription: Prescription.nullish(),
    prescriptionCommentsList: z.array(PrescriptionComments)
  })
]);

type PrescriptionCommentsData = z.infer<typeof PrescriptionCommentsData>;

type PrescriptionsState = {
  prescriptionFilters: PrescriptionFilters;
  prescriptionCommentsData?: PrescriptionCommentsData;
};

const initialState: PrescriptionsState = {
  prescriptionFilters: {
    missingDistribution: false,
    missingLaboratory: false,
    withNovelty: false
  }
};

const prescriptionsSlice = createSlice({
  name: 'prescriptions',
  initialState,
  reducers: {
    changePrescriptionFilters: (
      state,
      action: PayloadAction<PrescriptionFilters>
    ) => {
      state.prescriptionFilters = action.payload;
    },
    setPrescriptionCommentsData: (
      state,
      action: PayloadAction<PrescriptionCommentsData | undefined>
    ) => {
      state.prescriptionCommentsData = action.payload;
    },
    reset(): PrescriptionsState {
      return initialState;
    }
  }
});

export default prescriptionsSlice;
